import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AssetEditPopup from "./AssetEditPopup";

import {
  faTimes,
  faPlus,
  faSearch,
  faBoxOpen,
  faLaptop,
  faDesktop,
  faPhone,
  faHeadset,
  faKeyboard,
  faCircleCheck,
  faChevronDown,
  faChevronUp,
  faPen
} from "@fortawesome/free-solid-svg-icons";

function iconForType(type) {
  if (type === "Laptop") return faLaptop;
  if (type === "Monitor") return faDesktop;
  if (type === "Phone") return faPhone;
  if (type === "Headset") return faHeadset;
  if (type === "Keyboard") return faKeyboard;
  return faBoxOpen;
}

export default function AssetAllocationPopup(props) {
  var {
    open,
    person,
    floorLabel,
    availableAssets,
    assignedAssets,
    unallocatedAssets,
    onClose,
    onSave
  } = props;

  var [localAssignedIds, setLocalAssignedIds] = useState([]);
  var [search, setSearch] = useState("");
  var [typeFilter, setTypeFilter] = useState("All");
  var [showUnallocated, setShowUnallocated] = useState(true);
  
  var [showAssetPopup, setShowAssetPopup] = useState(false);
  var [selectedPerson, setSelectedPerson] = useState(null);
  
  var [showAssetEdit, setShowAssetEdit] = useState(false);
  var [editingAsset, setEditingAsset] = useState(null);

  useEffect(
    function () {
      if (!open) return;

      var ids = [];
      (assignedAssets || []).forEach(function (a) {
        if (!a) return;
        ids.push(a.id);
      });

      setLocalAssignedIds(ids);
      setSearch("");
      setTypeFilter("All");
      setShowUnallocated(true);
    },
    [open, person, assignedAssets]
  );
  
  function openEdit(asset) {
    setEditingAsset(asset);
    setShowAssetEdit(true);
  }

  function saveAsset(updated) {
    console.log("SAVE ASSET", updated);
    setShowAssetEdit(false);
  }

  var allTypes = useMemo(function () {
    var map = {};
    var add = function (arr) {
      (arr || []).forEach(function (a) {
        if (!a || !a.type) return;
        map[a.type] = true;
      });
    };

    add(availableAssets);
    add(unallocatedAssets);
    add(assignedAssets);

    var out = Object.keys(map).sort();
    out.unshift("All");
    return out;
  }, [availableAssets, unallocatedAssets, assignedAssets]);

  function matchesFilters(asset) {
    if (!asset) return false;

    if (typeFilter !== "All") {
      if (asset.type !== typeFilter) return false;
    }

    if (search && search.trim().length > 0) {
      var s = search.trim().toLowerCase();
      var hay =
        String(asset.name || "").toLowerCase() +
        " " +
        String(asset.serial || "").toLowerCase() +
        " " +
        String(asset.type || "").toLowerCase();
      if (hay.indexOf(s) === -1) return false;
    }

    return true;
  }

  function isAssigned(assetId) {
    return localAssignedIds.indexOf(assetId) >= 0;
  }

  function assign(assetId) {
    if (isAssigned(assetId)) return;

    setLocalAssignedIds(function (prev) {
      var next = prev.slice(0);
      next.push(assetId);
      return next;
    });
  }

  function remove(assetId) {
    setLocalAssignedIds(function (prev) {
      return prev.filter(function (x) {
        return x !== assetId;
      });
    });
  }

  function buildLookup(arr) {
    var map = {};
    (arr || []).forEach(function (a) {
      if (!a) return;
      map[a.id] = a;
    });
    return map;
  }

  var assignedLookup = useMemo(
    function () {
      return buildLookup(assignedAssets);
    },
    [assignedAssets]
  );

  var availableLookup = useMemo(
    function () {
      return buildLookup(availableAssets);
    },
    [availableAssets]
  );

  var unallocatedLookup = useMemo(
    function () {
      return buildLookup(unallocatedAssets);
    },
    [unallocatedAssets]
  );

  var assignedList = useMemo(
    function () {
      var out = [];
      localAssignedIds.forEach(function (id) {
        var a = assignedLookup[id] || availableLookup[id] || unallocatedLookup[id];
        if (a) out.push(a);
      });

      out.sort(function (a, b) {
        return String(a.type || "").localeCompare(String(b.type || "")) || String(a.name || "").localeCompare(String(b.name || ""));
      });

      return out;
    },
    [localAssignedIds, assignedLookup, availableLookup, unallocatedLookup]
  );

  var availableFiltered = useMemo(
    function () {
      var out = [];
      (availableAssets || []).forEach(function (a) {
        if (!a) return;
        if (isAssigned(a.id)) return;
        if (!matchesFilters(a)) return;
        out.push(a);
      });

      out.sort(function (a, b) {
        return String(a.type || "").localeCompare(String(b.type || "")) || String(a.name || "").localeCompare(String(b.name || ""));
      });

      return out;
    },
    [availableAssets, search, typeFilter, localAssignedIds]
  );

  var unallocatedFiltered = useMemo(
    function () {
      var out = [];
      (unallocatedAssets || []).forEach(function (a) {
        if (!a) return;
        if (isAssigned(a.id)) return;
        if (!matchesFilters(a)) return;
        out.push(a);
      });

      out.sort(function (a, b) {
        return String(a.type || "").localeCompare(String(b.type || "")) || String(a.name || "").localeCompare(String(b.name || ""));
      });

      return out;
    },
    [unallocatedAssets, search, typeFilter, localAssignedIds]
  );

  function handleSave() {
    if (!person || !person.id) return;
    if (onSave) onSave(person.id, localAssignedIds);
  }

  var styles = {
    backdrop: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      zIndex: 6000
    },

    popup: {
      position: "absolute",
      top: "90px",
      left: "5%",
      width: "90%",
      bottom: "40px",
      background: "linear-gradient(180deg, #FFFFFF 0%, #E7E9EC 100%)",
      borderRadius: "10px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    },

    header: {
      padding: "18px 22px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      background: "linear-gradient(180deg, #FFFFFF 0%, #EEF1F5 100%)",
    },

    headerTitle: { fontSize: "18px", fontWeight: 700 },
    headerSub: { fontSize: "12px", color: "#555", marginTop: "2px" },

    closeBtn: {
      width: "36px",
      height: "36px",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.12)",
      background: "white",
      cursor: "pointer"
    },

    body: {
      flex: 1,
      display: "flex",
      overflow: "hidden",
      gap: "14px",
      padding: "14px"
    },

    leftCol: {
      width: "34%",
      minWidth: 0,
      background: "white",
      borderRadius: "10px",
      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
      border: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    },

    rightCol: {
      flex: 1,
      minWidth: 0,
      background: "white",
      borderRadius: "10px",
      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
      border: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    },

    colHeader: {
      padding: "12px 14px",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      background: "#f7f8fa",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
	  background: 'linear-gradient(180deg, rgba(66,133,244,1) 0%, rgba(30,88,182,1) 100%)',
	  color: '#fff'
    },

    colHeaderTitle: { fontSize: "13px", fontWeight: 700, color: "#fff" },
    colHeaderMeta: { fontSize: "12px", color: "#ddd" },

    controlsRow: {
      padding: "10px 14px",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      gap: "10px",
      alignItems: "center"
    },

    searchBox: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 10px",
      borderRadius: "10px",
      border: "1px solid rgba(0,0,0,0.12)",
      background: "#fff"
    },

    searchInput: {
      border: "none",
      outline: "none",
      width: "100%",
      fontSize: "13px"
    },

    typePills: {
      padding: "0 14px 12px 14px",
      display: "flex",
      flexWrap: "wrap",
      gap: "8px"
    },

    pill: {
      padding: "6px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      border: "1px solid rgba(0,0,0,0.12)",
      background: "#fff",
      cursor: "pointer",
      color: "#222"
    },

    pillActive: {
      padding: "6px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      border: "1px solid rgba(31,111,229,0.55)",
      background: "linear-gradient(90deg, #1f6fe5, #2a8df7)",
      cursor: "default",
      color: "#fff",
      fontWeight: 700
    },

    list: { flex: 1, overflowY: "auto", padding: "10px 14px" },

    empty: {
      padding: "18px",
      textAlign: "center",
      color: "#777",
      fontSize: "13px"
    },

    assetRow: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 10px",
      borderRadius: "10px",
      border: "1px solid rgba(0,0,0,0.08)",
      marginBottom: "10px",
      background: "#fff"
    },

    assetIcon: {
      width: "34px",
      height: "34px",
      borderRadius: "10px",
      background: "#f2f4f7",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#1f6fe5"
    },

    assetMain: { flex: 1, minWidth: 0 },
    assetName: { fontSize: "13px", fontWeight: 700, color: "#111" },
    assetMeta: { fontSize: "12px", color: "#666", marginTop: "2px" },

    btnAdd: {
      border: "none",
      background: "rgba(31,111,229,0.12)",
      color: "#1f6fe5",
      padding: "8px 10px",
      borderRadius: "10px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: 700
    },

    btnRemove: {
      border: "none",
      background: "rgba(217,48,37,0.10)",
      color: "#d93025",
      padding: "8px 10px",
      borderRadius: "10px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: 700
    },

    unallocatedHeader: {
      padding: "10px 14px",
      borderTop: "1px solid rgba(0,0,0,0.08)",
      background: "#f7f8fa",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer"
    },

    footer: {
      padding: "14px 18px",
      borderTop: "1px solid rgba(0,0,0,0.10)",
      background: "#fff",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },

    footerLeft: { fontSize: "12px", color: "#666" },

    btnCancel: {
      border: "1px solid rgba(0,0,0,0.18)",
      background: "#fff",
      padding: "9px 14px",
      borderRadius: "8px",
      cursor: "pointer",
      marginRight: "10px"
    },

    btnSave: {
      border: "none",
      background: "linear-gradient(90deg, #1f6fe5, #2a8df7)",
      color: "#fff",
      padding: "10px 16px",
      borderRadius: "8px",
      cursor: "pointer",
      fontWeight: 700
    },
	
	editPill:{
		border: "none",
	      background: "rgba(31,111,229,0.12)",
	      color: "#1f6fe5",
	      padding: "8px 10px",
	      borderRadius: "10px",
	      cursor: "pointer",
	      fontSize: "12px",
	      fontWeight: 700,
		  width:'80px'
	},
	
	editPillIcon: {
	      marginRight:'10px',
	       color: "#1f6fe5",
	    },
  };

  if (!open) return null;

  return (
    <div style={styles.backdrop}>
      <div style={styles.popup}>
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>Asset Allocation</div>
            <div style={styles.headerSub}>
              {person ? person.name : "No person selected"}
              {floorLabel ? " • " + floorLabel : ""}
              {person && person.deskCode ? " • Desk: " + person.deskCode : ""}
            </div>
          </div>

          <button style={styles.closeBtn} onClick={onClose} title="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div style={styles.body}>
          {/* ASSIGNED */}
          <div style={styles.leftCol}>
            <div style={styles.colHeader}>
              <div style={styles.colHeaderTitle}>Current Assets</div>
              <div style={styles.colHeaderMeta}>{assignedList.length} assigned</div>
            </div>

            <div style={styles.list}>
              {assignedList.length === 0 && (
                <div style={styles.empty}>No assets assigned yet</div>
              )}

              {assignedList.map(function (a) {
                return (
                  <div key={a.id} style={styles.assetRow}>
                    <div style={styles.assetIcon}>
                      <FontAwesomeIcon icon={iconForType(a.type)} />
                    </div>

                    <div style={styles.assetMain}>
                      <div style={styles.assetName}>{a.name}</div>
                      <div style={styles.assetMeta}>
                        {a.type}
                        {a.serial ? " • " + a.serial : ""}
                      </div>
                    </div>
					<button
					  style={styles.editPill}
					  onClick={openEdit}
					  title="Edit asset details"
					>
					  <FontAwesomeIcon icon={faPen} style={styles.editPillIcon} />
					  Edit
					</button>
                    <button
                      style={styles.btnRemove}
                      onClick={function () {
                        remove(a.id);
                      }}
                      title="Remove"
                    >
                      <FontAwesomeIcon icon={faTimes} /> Remove
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AVAILABLE */}
          <div style={styles.rightCol}>
            <div style={styles.colHeader}>
              <div style={styles.colHeaderTitle}>Available Assets</div>
              <div style={styles.colHeaderMeta}>{availableFiltered.length} available</div>
            </div>

            <div style={styles.controlsRow}>
              <div style={styles.searchBox}>
                <FontAwesomeIcon icon={faSearch} style={{ opacity: 0.7 }} />
                <input
                  value={search}
                  onChange={function (e) {
                    setSearch(e.target.value);
                  }}
                  placeholder="Search assets (name / serial / type)…"
                  style={styles.searchInput}
                />
              </div>

              <div style={{ fontSize: "12px", color: "#666" }}>
                <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#1f6fe5", marginRight: "6px" }} />
                Changes are local until Save
              </div>
            </div>

            <div style={styles.typePills}>
              {allTypes.map(function (t) {
                var active = typeFilter === t;
                return (
                  <div
                    key={t}
                    style={active ? styles.pillActive : styles.pill}
                    onClick={function () {
                      if (active) return;
                      setTypeFilter(t);
                    }}
                  >
                    {t}
                  </div>
                );
              })}
            </div>

            <div style={styles.list}>
              {availableFiltered.length === 0 && (
                <div style={styles.empty}>No matching assets available</div>
              )}

              {availableFiltered.map(function (a) {
                return (
                  <div key={a.id} style={styles.assetRow}>
                    <div style={styles.assetIcon}>
                      <FontAwesomeIcon icon={iconForType(a.type)} />
                    </div>

                    <div style={styles.assetMain}>
                      <div style={styles.assetName}>{a.name}</div>
                      <div style={styles.assetMeta}>
                        {a.type}
                        {a.serial ? " • " + a.serial : ""}
                      </div>
                    </div>

                    <button
                      style={styles.btnAdd}
                      onClick={function () {
                        assign(a.id);
                      }}
                      title="Assign"
                    >
                      <FontAwesomeIcon icon={faPlus} /> Assign
                    </button>
                  </div>
                );
              })}
            </div>

            {/* UNALLOCATED STASH */}
            <div
              style={styles.unallocatedHeader}
              onClick={function () {
                setShowUnallocated(!showUnallocated);
              }}
            >
              <div style={{ fontSize: "13px", fontWeight: 700, color: "#222" }}>
                Unallocated Assets (global)
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {unallocatedFiltered.length}{" "}
                <FontAwesomeIcon icon={showUnallocated ? faChevronDown : faChevronUp} style={{ marginLeft: "8px" }} />
              </div>
            </div>

            {showUnallocated && (
              <div style={{ maxHeight: "220px", overflowY: "auto", padding: "10px 14px" }}>
                {unallocatedFiltered.length === 0 && (
                  <div style={styles.empty}>No matching unallocated assets</div>
                )}

                {unallocatedFiltered.map(function (a) {
                  return (
                    <div key={a.id} style={styles.assetRow}>
                      <div style={styles.assetIcon}>
                        <FontAwesomeIcon icon={iconForType(a.type)} />
                      </div>

                      <div style={styles.assetMain}>
                        <div style={styles.assetName}>{a.name}</div>
                        <div style={styles.assetMeta}>
                          {a.type}
                          {a.serial ? " • " + a.serial : ""}
                        </div>
                      </div>

                      <button
                        style={styles.btnAdd}
                        onClick={function () {
                          assign(a.id);
                        }}
                        title="Assign"
                      >
                        <FontAwesomeIcon icon={faPlus} /> Assign
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <div style={styles.footerLeft}>
            {person ? (
              <span>
                Editing: <strong>{person.name}</strong> • Assigned assets:{" "}
                <strong>{assignedList.length}</strong>
              </span>
            ) : (
              <span>Select a person</span>
            )}
          </div>

          <div>
            <button style={styles.btnCancel} onClick={onClose}>
              Cancel
            </button>

            <button
              style={styles.btnSave}
              onClick={handleSave}
              disabled={!person || localAssignedIds.length === 0}
              title="Save allocation"
            >
              Save Allocation
            </button>
          </div>
        </div>
      </div>
	  <AssetEditPopup
	    open={showAssetEdit}
	    asset={editingAsset}
	    contextText={"Jane Smith • HQ Building • Floor 1 • Desk RF.08.M12.06"}
	    onClose={function () { setShowAssetEdit(false); }}
	    onSave={saveAsset}
	  />
    </div>
  );
}
